import type { ChangeEvent } from 'react';

import type {
  Cluster,
  ClusteringRunParams,
  ClusteringRunResponse,
  LatLongTuple,
  ManualCluster,
  ManualClusterValidation,
  NoiseSummary,
} from '~/components/LERE/types';

export function pickParamsJson(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (value && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

export function getNoiseSummary(previewData?: ClusteringRunResponse | null): NoiseSummary | null {
  if (!previewData) {
    return null;
  }
  const noiseCount = previewData.noise_count;
  const totalPoints = previewData.assignments_count;
  if (typeof noiseCount !== 'number' || typeof totalPoints !== 'number' || totalPoints <= 0) {
    return null;
  }
  const noisePct = typeof previewData.noise_pct === 'number' ? previewData.noise_pct : (noiseCount / totalPoints) * 100;
  return { noiseCount, totalPoints, noisePct };
}

export function parseParamsJson(paramsJson?: string | null): ClusteringRunParams | null {
  if (!paramsJson) {
    return null;
  }
  try {
    const parsed = JSON.parse(paramsJson);
    if (typeof parsed === 'string') {
      try {
        const nested = JSON.parse(parsed);
        if (nested && typeof nested === 'object') {
          return nested as ClusteringRunParams;
        }
        return { raw_params_json: parsed };
      } catch {
        return { raw_params_json: parsed };
      }
    }
    if (parsed && typeof parsed === 'object') {
      return parsed as ClusteringRunParams;
    }
  } catch {
    return { raw_params_json: paramsJson };
  }
  return { raw_params_json: paramsJson };
}

export function getVisibleClusters(
  clusters: Array<Cluster>,
  minClusterOffers: number,
  maxClusters: number,
): Array<Cluster> {
  let filtered = clusters;
  if (minClusterOffers > 0) {
    filtered = filtered.filter((cluster) => cluster.offer_count >= minClusterOffers);
  }
  if (maxClusters > 0 && filtered.length > maxClusters) {
    filtered = [...filtered].sort((a, b) => b.offer_count - a.offer_count).slice(0, maxClusters);
  }
  return filtered;
}

export function buildManualClusterLabels(
  clusters: Array<Cluster>,
  manualClusters: Array<ManualCluster>,
): Map<number, string> {
  if (!clusters.length || manualClusters.length === 0) {
    return new Map<number, string>();
  }
  const labels = new Map<number, string>();
  for (const cluster of clusters) {
    for (const region of manualClusters) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
      if (region.enabled === false) {
        continue;
      }
      if (isPointInManualCluster([cluster.centroid_latitude, cluster.centroid_longitude], region)) {
        labels.set(cluster.cluster_id, region.name);
        break;
      }
    }
  }
  return labels;
}

export function getManualClusterIds(
  clusters: Array<Cluster>,
  manualClusters: Array<ManualCluster>,
): Array<Array<number>> {
  if (!manualClusters.length) {
    return [];
  }
  const assignedClusterIds = new Set<number>();
  return manualClusters.map((region) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
    if (region.enabled === false) {
      return [];
    }
    const ids = clusters
      .filter((cluster) => {
        if (assignedClusterIds.has(cluster.cluster_id)) {
          return false;
        }
        return isPointInManualCluster([cluster.centroid_latitude, cluster.centroid_longitude], region);
      })
      .map((cluster) => cluster.cluster_id);

    ids.forEach((clusterId) => assignedClusterIds.add(clusterId));
    return Array.from(new Set(ids)).sort((a, b) => a - b);
  });
}

export function isPointInManualCluster(point: LatLongTuple, region: ManualCluster): boolean {
  if (!region.polygon || region.polygon.length < 3) {
    return false;
  }
  return isPointInPolygon(point, region.polygon);
}

export function isPointInPolygon(point: LatLongTuple, polygon: Array<LatLongTuple>): boolean {
  if (polygon.length < 3) {
    return false;
  }
  const [lat, lon] = point;
  const last = polygon[polygon.length - 1];
  const isClosed = last ? polygon[0][0] === last[0] && polygon[0][1] === last[1] : false;
  const cleaned = isClosed ? polygon.slice(0, -1) : polygon;
  let inside = false;
  let j = cleaned.length - 1;
  for (let i = 0; i < cleaned.length; i += 1) {
    const [latI, lonI] = cleaned[i];
    const [latJ, lonJ] = cleaned[j];
    const intersects = latI > lat !== latJ > lat && lon < ((lonJ - lonI) * (lat - latI)) / (latJ - latI) + lonI;
    if (intersects) {
      inside = !inside;
    }
    j = i;
  }
  return inside;
}

export function getManualClusterValidation(cluster: ManualCluster): ManualClusterValidation {
  const hasPolygon = cluster.polygon.length >= 3;
  const polygonInRange =
    hasPolygon && cluster.polygon.every(([lat, lon]) => lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180);
  return { hasPolygon, polygonInRange };
}

export function getPolygonBounds(polygon: Array<LatLongTuple>) {
  if (!polygon.length) {
    return null;
  }
  const lats = polygon.map((point) => point[0]);
  const lons = polygon.map((point) => point[1]);
  const latMin = Math.min(...lats);
  const latMax = Math.max(...lats);
  const lonMin = Math.min(...lons);
  const lonMax = Math.max(...lons);
  return { latMin, latMax, lonMin, lonMax };
}

export function clampOpacity(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

export function onNumberChange(handler: (value: number) => void) {
  return (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handler(Number(event.target.value));
  };
}

const CLUSTER_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFEAA7', // Yellow
  '#DDA0DD', // Plum
  '#98D8C8', // Mint
  '#F7DC6F', // Gold
  '#BB8FCE', // Purple
  '#85C1E9', // Sky Blue
  '#F8B500', // Amber
  '#00CED1', // Dark Cyan
  '#FF7F50', // Coral
  '#9ACD32', // Yellow Green
  '#DA70D6', // Orchid
  '#20B2AA', // Light Sea Green
  '#FF69B4', // Hot Pink
  '#7B68EE', // Medium Slate Blue
  '#3CB371', // Medium Sea Green
  '#FFD700', // Gold
];

export function getClusterColor(clusterId: number): string {
  return CLUSTER_COLORS[clusterId % CLUSTER_COLORS.length];
}
